import { createAsyncThunk } from '@reduxjs/toolkit';
import { StatisticsActions } from 'application/constants/actions';
import { AxiosError } from 'axios';
import { StatisticsApi } from 'integration/api/statistics';
import {
  FiltersCitiesQueryParamsDto,
  FiltersClinicsQueryParamsDto,
  PlatformStatisticsQueryParamsDto,
  StatsPerCityQueryParamsDto,
  StatsPerClinicsQueryParamsDto,
  StatsPerProfessionalsQueryParamsDto,
} from '@docbay/schemas';

/**
 * Fetches cities based on the given query parameters.
 *
 * @param params - The query parameters for filtering cities.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchCities = createAsyncThunk(
  StatisticsActions.fetchCities,
  async (params: FiltersCitiesQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await StatisticsApi.fetchCities(params);

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches clinics based on the given query parameters.
 *
 * @param params - The query parameters for filtering clinics.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchClinics = createAsyncThunk(
  StatisticsActions.fetchClinics,
  async (params: FiltersClinicsQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await StatisticsApi.fetchClinics(params);

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches metrics per city based on the given query parameters.
 *
 * @param params - The query parameters for fetching city metrics.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchPerCityMetrics = createAsyncThunk(
  StatisticsActions.fetchPerCityMetrics,
  async (params: StatsPerCityQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await StatisticsApi.fetchPerCityMetrics(
        params,
      );

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches metrics per clinic based on the given query parameters.
 *
 * @param params - The query parameters for fetching clinic metrics.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchPerClinicsMetrics = createAsyncThunk(
  StatisticsActions.fetchPerClinicsMetrics,
  async (params: StatsPerClinicsQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await StatisticsApi.fetchPerClinicsMetrics(
        params,
      );

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches metrics per doctor based on the given query parameters.
 *
 * @param params - The query parameters for fetching doctor metrics.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchPerDoctorsMetrics = createAsyncThunk(
  StatisticsActions.fetchPerDoctorsMetrics,
  async (params: StatsPerProfessionalsQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await StatisticsApi.fetchPerDoctorsMetrics(
        params,
      );

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches specializations.
 *
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchSpecializations = createAsyncThunk(
  StatisticsActions.fetchSpecializations,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await StatisticsApi.fetchSpecializations();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches platform statistics based on the given query parameters.
 *
 * @param params - The query parameters for fetching platform statistics.
 * @returns A promise that resolves with the response data or rejects with an error message if the request fails.
 */
export const fetchPlatformStatistics = createAsyncThunk(
  StatisticsActions.fetchPlatformStatistics,
  async (params: PlatformStatisticsQueryParamsDto, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await StatisticsApi.fetchPlatformStatistics(params);

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
